import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AdminNav from './AdminNav';
import { FaSave, FaCog, FaCreditCard, FaShippingFast, FaPercentage, FaEnvelope, FaUsers, FaLock } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const SettingsContainer = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 250px;
  flex-shrink: 0;
  
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const Content = styled.div`
  flex-grow: 1;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SidebarItem = styled.li`
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SidebarButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 1rem;
  text-align: left;
  background-color: ${props => props.active ? '#f8f9fa' : 'white'};
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  svg {
    margin-right: 0.75rem;
    color: ${props => props.active ? '#C41E3A' : '#666'};
  }
  
  span {
    font-weight: ${props => props.active ? '600' : '400'};
    color: ${props => props.active ? '#C41E3A' : '#333'};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0.5rem;
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  background-color: #C41E3A;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #A01930;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const SectionTitle = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 1}, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HelpText = styled.p`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const SettingsPage = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // General Settings
    storeName: 'Master Collectors Forge',
    storeEmail: 'contact@mastercollectorsforge.com',
    storePhone: '(555) 123-4567',
    storeAddress: '123 Main St, Anytown, USA',
    businessHours: 'Mon-Fri: 9am-5pm, Sat: 10am-4pm, Sun: Closed',
    
    // Payment Settings
    acceptCreditCards: true,
    acceptPayPal: true,
    acceptCrypto: false,
    stripePublishableKey: '',
    paypalClientId: '',
    
    // Shipping Settings
    enableShipping: true,
    defaultShippingMethod: 'standard',
    freeShippingEnabled: true,
    freeShippingThreshold: 75.00,
    shippoEnabled: true,
    defaultFromAddress: {
      name: 'Master Collectors Forge',
      street1: '123 Main St',
      city: 'Anytown',
      state: 'CA',
      zip: '90210',
      country: 'US',
      phone: '555-123-4567',
      email: 'shipping@mastercollectorsforge.com'
    },
    // Tax Settings
    enableTax: true,
    taxRate: 7.5,
    
    // Email Settings
    orderConfirmationTemplate: 'Thank you for your order! Your order #{{orderNumber}} has been received and is being processed.',
    shippingConfirmationTemplate: 'Good news! Your order #{{orderNumber}} has been shipped and is on its way to you.',
    
    // User Settings
    allowUserRegistration: true,
    requireEmailVerification: true,
    
    // Security Settings
    enableTwoFactorAuth: false,
    passwordMinLength: 8,
    passwordRequireSpecialChar: true
  });
  
  // Fetch settings from Firestore
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Fetch settings from Firestore
        const settingsDoc = doc(db, 'settings', 'storeSettings');
        const settingsSnapshot = await getDoc(settingsDoc);
        
        if (settingsSnapshot.exists()) {
          setSettings(settingsSnapshot.data());
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching settings:', error);
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle nested objects like defaultFromAddress.name
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setSettings(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };
  
  // Handle number input changes
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: parseFloat(value)
    }));
  };
  
  // Save settings to Firestore
  const saveSettings = async () => {
    setSaving(true);
    try {
      // Save to Firestore
      const settingsDoc = doc(db, 'settings', 'storeSettings');
      
      // Add updatedAt timestamp
      const updatedSettings = {
        ...settings,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(settingsDoc, updatedSettings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Render different settings sections
  const renderSettingsContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <>
            <SectionTitle>General Settings</SectionTitle>
            <FormGroup>
              <Label htmlFor="storeName">Store Name</Label>
              <Input
                type="text"
                id="storeName"
                name="storeName"
                value={settings.storeName}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormRow columns={2}>
              <FormGroup>
                <Label htmlFor="storeEmail">Email Address</Label>
                <Input
                  type="email"
                  id="storeEmail"
                  name="storeEmail"
                  value={settings.storeEmail}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="storePhone">Phone Number</Label>
                <Input
                  type="tel"
                  id="storePhone"
                  name="storePhone"
                  value={settings.storePhone}
                  onChange={handleChange}
                />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label htmlFor="storeAddress">Store Address</Label>
              <Input
                type="text"
                id="storeAddress"
                name="storeAddress"
                value={settings.storeAddress}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="businessHours">Business Hours</Label>
              <Input
                type="text"
                id="businessHours"
                name="businessHours"
                value={settings.businessHours}
                onChange={handleChange}
              />
              <HelpText>Format: Mon-Fri: 9am-5pm, Sat: 10am-4pm, Sun: Closed</HelpText>
            </FormGroup>
          </>
        );
        
      case 'payment':
        return (
          <>
            <SectionTitle>Payment Settings</SectionTitle>
            <FormGroup>
              <Label>Accepted Payment Methods</Label>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="acceptCreditCards"
                  checked={settings.acceptCreditCards}
                  onChange={handleChange}
                />
                Credit Cards (Visa, Mastercard, Amex)
              </CheckboxLabel>
              
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="acceptPayPal"
                  checked={settings.acceptPayPal}
                  onChange={handleChange}
                />
                PayPal
              </CheckboxLabel>
              
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="acceptCrypto"
                  checked={settings.acceptCrypto}
                  onChange={handleChange}
                />
                Cryptocurrency
              </CheckboxLabel>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="stripePublishableKey">Stripe Publishable Key</Label>
              <Input
                type="text"
                id="stripePublishableKey"
                name="stripePublishableKey"
                value={settings.stripePublishableKey}
                onChange={handleChange}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="paypalClientId">PayPal Client ID</Label>
              <Input
                type="text"
                id="paypalClientId"
                name="paypalClientId"
                value={settings.paypalClientId}
                onChange={handleChange}
              />
            </FormGroup>
          </>
        );
        
      case 'shipping':
        return (
          <>
            <SectionTitle>Shipping Settings</SectionTitle>
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="enableShipping"
                  checked={settings.enableShipping}
                  onChange={handleChange}
                />
                Enable Shipping
              </CheckboxLabel>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="defaultShippingMethod">Default Shipping Method</Label>
              <Select
                id="defaultShippingMethod"
                name="defaultShippingMethod"
                value={settings.defaultShippingMethod}
                onChange={handleChange}
              >
                <option value="standard">Standard Shipping</option>
                <option value="express">Express Shipping</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="freeShippingEnabled"
                  checked={settings.freeShippingEnabled}
                  onChange={handleChange}
                />
                Enable Free Shipping
              </CheckboxLabel>
            </FormGroup>
            
            {settings.freeShippingEnabled && (
              <FormGroup>
                <Label htmlFor="freeShippingThreshold">Free Shipping Threshold ($)</Label>
                <Input
                  type="number"
                  id="freeShippingThreshold"
                  name="freeShippingThreshold"
                  value={settings.freeShippingThreshold}
                  onChange={handleNumberChange}
                  min="0"
                  step="0.01"
                />
                <HelpText>Orders above this amount qualify for free shipping</HelpText>
              </FormGroup>
            )}
            
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="shippoEnabled"
                  checked={settings.shippoEnabled}
                  onChange={handleChange}
                />
                Enable Shippo Integration
              </CheckboxLabel>
            </FormGroup>
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.name">Default From Address - Name</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.name"
                  name="defaultFromAddress.name"
                  value={settings.defaultFromAddress.name}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.street1">Default From Address - Street 1</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.street1"
                  name="defaultFromAddress.street1"
                  value={settings.defaultFromAddress.street1}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.city">Default From Address - City</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.city"
                  name="defaultFromAddress.city"
                  value={settings.defaultFromAddress.city}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.state">Default From Address - State</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.state"
                  name="defaultFromAddress.state"
                  value={settings.defaultFromAddress.state}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.zip">Default From Address - Zip</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.zip"
                  name="defaultFromAddress.zip"
                  value={settings.defaultFromAddress.zip}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.country">Default From Address - Country</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.country"
                  name="defaultFromAddress.country"
                  value={settings.defaultFromAddress.country}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.phone">Default From Address - Phone</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.phone"
                  name="defaultFromAddress.phone"
                  value={settings.defaultFromAddress.phone}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
            
            {settings.shippoEnabled && (
              <FormGroup>
                <Label htmlFor="defaultFromAddress.email">Default From Address - Email</Label>
                <Input
                  type="text"
                  id="defaultFromAddress.email"
                  name="defaultFromAddress.email"
                  value={settings.defaultFromAddress.email}
                  onChange={handleChange}
                />
              </FormGroup>
            )}
          </>
        );
        
      case 'tax':
        return (
          <>
            <SectionTitle>Tax Settings</SectionTitle>
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="enableTax"
                  checked={settings.enableTax}
                  onChange={handleChange}
                />
                Enable Tax Calculation
              </CheckboxLabel>
            </FormGroup>
            
            {settings.enableTax && (
              <FormGroup>
                <Label htmlFor="taxRate">Default Tax Rate (%)</Label>
                <Input
                  type="number"
                  id="taxRate"
                  name="taxRate"
                  value={settings.taxRate}
                  onChange={handleNumberChange}
                  min="0"
                  max="100"
                  step="0.1"
                />
              </FormGroup>
            )}
          </>
        );
        
      case 'email':
        return (
          <>
            <SectionTitle>Email Notification Templates</SectionTitle>
            <FormGroup>
              <Label htmlFor="orderConfirmationTemplate">Order Confirmation Email</Label>
              <Textarea
                id="orderConfirmationTemplate"
                name="orderConfirmationTemplate"
                value={settings.orderConfirmationTemplate}
                onChange={handleChange}
              />
              <HelpText>Use {'{{'} orderNumber {'}}' }, {'{{'} customerName {'}}' }, etc. as placeholders</HelpText>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="shippingConfirmationTemplate">Shipping Confirmation Email</Label>
              <Textarea
                id="shippingConfirmationTemplate"
                name="shippingConfirmationTemplate"
                value={settings.shippingConfirmationTemplate}
                onChange={handleChange}
              />
              <HelpText>Use {'{{'} orderNumber {'}}' }, {'{{'} trackingNumber {'}}' }, etc. as placeholders</HelpText>
            </FormGroup>
          </>
        );
        
      case 'users':
        return (
          <>
            <SectionTitle>User Account Settings</SectionTitle>
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="allowUserRegistration"
                  checked={settings.allowUserRegistration}
                  onChange={handleChange}
                />
                Allow User Registration
              </CheckboxLabel>
            </FormGroup>
            
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="requireEmailVerification"
                  checked={settings.requireEmailVerification}
                  onChange={handleChange}
                />
                Require Email Verification
              </CheckboxLabel>
            </FormGroup>
          </>
        );
        
      case 'security':
        return (
          <>
            <SectionTitle>Security Settings</SectionTitle>
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="enableTwoFactorAuth"
                  checked={settings.enableTwoFactorAuth}
                  onChange={handleChange}
                />
                Enable Two-Factor Authentication
              </CheckboxLabel>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="passwordMinLength">Minimum Password Length</Label>
              <Input
                type="number"
                id="passwordMinLength"
                name="passwordMinLength"
                value={settings.passwordMinLength}
                onChange={handleNumberChange}
                min="6"
              />
            </FormGroup>
            
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="passwordRequireSpecialChar"
                  checked={settings.passwordRequireSpecialChar}
                  onChange={handleChange}
                />
                Require Special Characters in Password
              </CheckboxLabel>
            </FormGroup>
          </>
        );
        
      default:
        return <p>Select a settings category</p>;
    }
  };
  
  if (loading) {
    return <Container>Loading settings...</Container>;
  }
  
  return (
    <Container>
      <Header>
        <Title>Store Settings</Title>
        <Subtitle>Configure your store preferences and options</Subtitle>
      </Header>
      
      <AdminNav />
      
      <SettingsContainer>
        <Sidebar>
          <SidebarMenu>
            <SidebarItem>
              <SidebarButton 
                active={activeSection === 'general'} 
                onClick={() => setActiveSection('general')}
              >
                <FaCog />
                <span>General</span>
              </SidebarButton>
            </SidebarItem>
            <SidebarItem>
              <SidebarButton 
                active={activeSection === 'payment'} 
                onClick={() => setActiveSection('payment')}
              >
                <FaCreditCard />
                <span>Payment</span>
              </SidebarButton>
            </SidebarItem>
            <SidebarItem>
              <SidebarButton 
                active={activeSection === 'shipping'} 
                onClick={() => setActiveSection('shipping')}
              >
                <FaShippingFast />
                <span>Shipping</span>
              </SidebarButton>
            </SidebarItem>
            <SidebarItem>
              <SidebarButton 
                active={activeSection === 'tax'} 
                onClick={() => setActiveSection('tax')}
              >
                <FaPercentage />
                <span>Tax</span>
              </SidebarButton>
            </SidebarItem>
            <SidebarItem>
              <SidebarButton 
                active={activeSection === 'email'} 
                onClick={() => setActiveSection('email')}
              >
                <FaEnvelope />
                <span>Email Templates</span>
              </SidebarButton>
            </SidebarItem>
            <SidebarItem>
              <SidebarButton 
                active={activeSection === 'users'} 
                onClick={() => setActiveSection('users')}
              >
                <FaUsers />
                <span>User Accounts</span>
              </SidebarButton>
            </SidebarItem>
            <SidebarItem>
              <SidebarButton 
                active={activeSection === 'security'} 
                onClick={() => setActiveSection('security')}
              >
                <FaLock />
                <span>Security</span>
              </SidebarButton>
            </SidebarItem>
          </SidebarMenu>
        </Sidebar>
        
        <Content>
          {renderSettingsContent()}
          
          <Button onClick={saveSettings} disabled={saving}>
            <FaSave />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Content>
      </SettingsContainer>
    </Container>
  );
};

export default SettingsPage;
